/*
 * Styles for the redesigned marketing + login page (/auth/new-login).
 * On desktop the page is a single, non-scrolling screen; below "sm" it falls back
 * to normal scrolling so the login form is always reachable on phones.
 * The page owns its full background, so it is rendered outside the Auth layout's
 * dark-overlay wrapper — everything below is self-contained.
 */
const INK = "#0A1020";
const INK_SOFT = "#131C2F";
const BRAND = "#E73F3B";
const BODY = "#4A5568";

// Short laptop screens: tighten spacing so everything still fits in one viewport.
const SHORT_SCREEN = "@media (min-width: 960px) and (max-height: 760px)";

const newLoginPageStyle = (theme) => ({
  "@keyframes ecgDraw": {
    "0%": { strokeDashoffset: 1400 },
    "60%": { strokeDashoffset: 0 },
    "100%": { strokeDashoffset: 0, opacity: 0.15 },
  },
  "@keyframes pulseRing": {
    "0%": { transform: "scale(0.75)", opacity: 0.55 },
    "70%": { transform: "scale(1.25)", opacity: 0 },
    "100%": { transform: "scale(1.25)", opacity: 0 },
  },
  "@keyframes riseIn": {
    from: { opacity: 0, transform: "translateY(18px)" },
    to: { opacity: 1, transform: "translateY(0)" },
  },

  page: {
    backgroundColor: INK,
    height: "100vh",
    overflow: "hidden",
    width: "100%",
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    [theme.breakpoints.down("sm")]: {
      overflow: "visible",
      minHeight: "100vh",
      height: "auto",
    },
  },

  /* ---------------- hero ---------------- */
  hero: {
    background: `radial-gradient(1200px 600px at 12% -10%, rgba(231,63,59,0.28) 0%, rgba(231,63,59,0) 55%),
                 radial-gradient(900px 500px at 100% 0%, rgba(63,122,231,0.22) 0%, rgba(63,122,231,0) 55%),
                 linear-gradient(160deg, ${INK} 0%, ${INK_SOFT} 100%)`,
    position: "relative",
    overflow: "hidden",
    color: "#FFFFFF",
    height: "100%",
    [theme.breakpoints.down("sm")]: {
      minHeight: "100vh",
      height: "auto",
    },
  },
  heroGrid: {
    backgroundImage:
      "linear-gradient(rgba(255,255,255,0.045) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.045) 1px, transparent 1px)",
    backgroundSize: "56px 56px",
    position: "absolute",
    pointerEvents: "none",
    bottom: 0,
    right: 0,
    left: 0,
    top: 0,
  },
  ecg: {
    position: "absolute",
    pointerEvents: "none",
    opacity: 0.5,
    height: "220px",
    bottom: "6%",
    width: "60%",
    left: 0,
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },
  ecgLine: {
    strokeDasharray: 1400,
    animation: "$ecgDraw 6s ease-in-out infinite",
  },
  container: {
    flexDirection: "column",
    padding: "0 24px",
    maxWidth: "1180px",
    position: "relative",
    display: "flex",
    margin: "0 auto",
    height: "100%",
    zIndex: 3,
  },

  /* ---------------- top bar ---------------- */
  topBar: {
    justifyContent: "space-between",
    padding: "22px 0 6px",
    alignItems: "center",
    display: "flex",
  },
  topLogo: {
    display: "block",
    width: "130px",
  },
  topLink: {
    textDecoration: "none",
    letterSpacing: "0.2px",
    alignItems: "center",
    color: "rgba(255,255,255,0.72)",
    fontSize: "13px",
    display: "flex",
    gap: "6px",
    "&:hover": {
      color: "#FFFFFF",
    },
    [theme.breakpoints.down("xs")]: {
      display: "none",
    },
  },

  /* ---------------- hero content ---------------- */
  heroInner: {
    padding: "16px 0 32px",
    alignItems: "center",
    display: "flex",
    minHeight: 0,
    gap: "48px",
    flex: 1,
    [SHORT_SCREEN]: {
      padding: "8px 0 20px",
    },
    [theme.breakpoints.down("sm")]: {
      padding: "24px 0 48px",
      flexDirection: "column",
      alignItems: "stretch",
      minHeight: "0",
      gap: "32px",
    },
  },
  heroCopy: {
    animation: "$riseIn 700ms ease both",
    maxWidth: "600px",
    flex: "1 1 55%",
    [theme.breakpoints.down("sm")]: {
      order: 2,
    },
  },
  heroForm: {
    flex: "0 0 400px",
    width: "400px",
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      flex: "1 1 auto",
      order: 1,
    },
  },
  eyebrow: {
    border: "1px solid rgba(231,63,59,0.45)",
    backgroundColor: "rgba(231,63,59,0.12)",
    textTransform: "uppercase",
    display: "inline-flex",
    letterSpacing: "1.4px",
    alignItems: "center",
    borderRadius: "999px",
    padding: "6px 14px",
    fontWeight: 600,
    fontSize: "11px",
    color: "#FF8A87",
    gap: "8px",
  },
  heroTitle: {
    fontSize: "46px",
    lineHeight: 1.12,
    fontWeight: 700,
    letterSpacing: "-0.6px",
    margin: "22px 0 0",
    color: "#FFFFFF",
    [theme.breakpoints.down("md")]: {
      fontSize: "38px",
    },
    [SHORT_SCREEN]: {
      margin: "16px 0 0",
      fontSize: "36px",
    },
    [theme.breakpoints.down("xs")]: {
      fontSize: "30px",
      margin: "16px 0 0",
    },
  },
  heroAccent: {
    color: BRAND,
  },
  heroLead: {
    color: "rgba(255,255,255,0.72)",
    maxWidth: "540px",
    lineHeight: 1.65,
    fontSize: "16px",
    margin: "18px 0 0",
    [SHORT_SCREEN]: {
      margin: "12px 0 0",
      fontSize: "14.5px",
    },
  },
  highlightRow: {
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    margin: "30px 0 0",
    display: "grid",
    gap: "14px",
    [SHORT_SCREEN]: {
      margin: "20px 0 0",
    },
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "minmax(0, 1fr)",
    },
  },
  highlight: {
    border: "1px solid rgba(255,255,255,0.12)",
    backgroundColor: "rgba(255,255,255,0.05)",
    transition: "border-color 0.25s ease, background-color 0.25s ease",
    borderRadius: "12px",
    padding: "16px",
    "&:hover": {
      backgroundColor: "rgba(255,255,255,0.08)",
      borderColor: "rgba(231,63,59,0.45)",
    },
    [SHORT_SCREEN]: {
      padding: "12px 14px",
    },
  },
  highlightIcon: {
    backgroundColor: "rgba(231,63,59,0.14)",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: "10px",
    marginBottom: "12px",
    display: "flex",
    height: "38px",
    width: "38px",
    [SHORT_SCREEN]: {
      marginBottom: "8px",
      height: "32px",
      width: "32px",
    },
  },
  highlightTitle: {
    color: "#FFFFFF",
    margin: "0 0 6px",
    fontSize: "15px",
    fontWeight: 700,
  },
  highlightText: {
    color: "rgba(255,255,255,0.6)",
    lineHeight: 1.5,
    fontSize: "12.5px",
    margin: 0,
  },

  /* ---------------- login card ---------------- */
  card: {
    boxShadow: "0 24px 60px rgba(4, 8, 20, 0.45)",
    animation: "$riseIn 700ms ease both",
    backgroundColor: "#FFFFFF",
    animationDelay: "120ms",
    borderRadius: "16px",
    padding: "30px 28px",
    position: "relative",
    [SHORT_SCREEN]: {
      padding: "22px 24px",
    },
    [theme.breakpoints.down("xs")]: {
      padding: "24px 20px",
    },
  },
  cardTitle: {
    letterSpacing: "-0.3px",
    color: "#1A202C",
    margin: "0 0 4px",
    fontWeight: 700,
    fontSize: "22px",
  },
  cardSubtitle: {
    margin: "0 0 14px",
    fontSize: "13.5px",
    lineHeight: 1.5,
    color: BODY,
  },
  cardHint: {
    borderLeft: `3px solid ${BRAND}`,
    backgroundColor: "#FDF3F3",
    borderRadius: "0 8px 8px 0",
    margin: "0 0 16px",
    padding: "10px 12px",
    lineHeight: 1.5,
    fontSize: "12.5px",
    color: "#7B3B39",
  },
  versionBox: {
    margin: "0 0 12px",
  },
  inputAdornmentIcon: {
    color: "#A0A8B8",
  },
  actions: {
    margin: "22px 0 0",
  },
  linkButton: {
    textTransform: "none",
    background: "none",
    cursor: "pointer",
    fontWeight: 600,
    fontSize: "13px",
    padding: "10px 0",
    border: "none",
    width: "100%",
    color: BODY,
    "&:hover": {
      color: BRAND,
    },
  },
  cardFooter: {
    borderTop: "1px solid #EDF0F5",
    justifyContent: "space-between",
    margin: "18px 0 0",
    padding: "14px 0 0",
    alignItems: "center",
    color: "#8A94A6",
    fontSize: "11.5px",
    display: "flex",
  },
  cardFooterMark: {
    alignItems: "center",
    fontWeight: 600,
    display: "flex",
    color: "#4A5568",
    gap: "6px",
  },

});

export default newLoginPageStyle;
